import { lazy } from 'react';

import type { RouteObject } from 'react-router-dom';

/**
 * Rotas do módulo de instituições. O perfil é público, sem guard: convidado
 * vê instituições (P2). Criar é produzir conteúdo — exige conta, e por isso a
 * lista protegida entra sob `RequireSession` na árvore raiz.
 *
 * `institutions` (a entrada do módulo, alcançada pelo menu) fica **sem** guard
 * de propósito: ela é o destino de um item de menu, e um item de menu que
 * arremessa o convidado para o login antes de dizer o que existe do outro lado
 * é uma porta que se fecha sem explicar. A tela mostra o convite e o `?next=`.
 */

const MyInstitutionsPage = lazy(() => import('./routes/MyInstitutions.page'));
const InstitutionDirectoryPage = lazy(() => import('./routes/InstitutionDirectory.page'));
const OpeningsBoardPage = lazy(() => import('./routes/OpeningsBoard.page'));
const OpeningPage = lazy(() => import('./routes/Opening.page'));
const InstitutionPage = lazy(() => import('./routes/Institution.page'));
const CreateInstitutionPage = lazy(() => import('./routes/CreateInstitution.page'));
const ManageInstitutionPage = lazy(() => import('./routes/ManageInstitution.page'));
const InstitutionOpeningsPage = lazy(() => import('./routes/InstitutionOpenings.page'));
const InstitutionTeamPage = lazy(() => import('./routes/InstitutionTeam.page'));
const ManageVolunteersPage = lazy(() => import('./routes/ManageVolunteers.page'));

export const institutionsRoutes: RouteObject[] = [
  { path: 'institutions', element: <MyInstitutionsPage /> },
  // O **mural de vagas** (planilha, 2026-09-20). Sem guard, como o perfil: a
  // RLS de `volunteer_openings` é leitura pública, e mandar quem quer ajudar
  // fazer login antes de ver o que existe perderia a pessoa na porta. É aqui, e
  // não no módulo de chamados, porque a vaga é da instituição — o schema, o
  // service e o formulário de publicação já moram neste módulo.
  { path: 'openings', element: <OpeningsBoardPage /> },
  // Lista pública de instituições (T06). Estática, então ganha de `:id` no ranking.
  { path: 'institutions/directory', element: <InstitutionDirectoryPage /> },
  // Detalhes da vaga (fila 2026-10-31, T16): onde "Suas candidaturas" leva. Sem
  // guard pelo mesmo motivo do mural — a vaga é leitura pública.
  { path: 'openings/:id', element: <OpeningPage /> },
  // `new` antes de `:id` por clareza; o react-router já ranqueia o segmento
  // estático acima do dinâmico, então a ordem aqui é documentação.
  { path: 'institutions/:id', element: <InstitutionPage /> },
];

export const institutionsProtectedRoutes: RouteObject[] = [
  { path: 'institutions/new', element: <CreateInstitutionPage /> },
  // A tela ainda checa o papel de admin — o guard só garante sessão.
  { path: 'institutions/:id/manage', element: <ManageInstitutionPage /> },
  // "Gerenciar voluntários" (planilha, 2026-09-20): a lista de membros que
  // morava dentro da gestão, agora alcançável direto do perfil da instituição.
  // O nome da rota é o do produto; o agregado por baixo continua sendo
  // `institution_members`.
  { path: 'institutions/:id/volunteers', element: <ManageVolunteersPage /> },
  // Publicar vaga e decidir candidatura: formulário e decisão sobre pessoas, o
  // mesmo tipo de fluxo de tarefa que a gestão — fora do `AppLayout`.
  { path: 'institutions/:id/openings', element: <InstitutionOpeningsPage /> },
  // A equipe, para quem **participa** e não administra: é onde voluntário
  // avalia colega (DV-12). A gestão tem a própria lista de membros; esta existe
  // porque, sem ela, avaliar era privilégio de quem administra — o oposto do
  // que a decisão diz.
  { path: 'institutions/:id/team', element: <InstitutionTeamPage /> },
];
